import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Country, Option, TranslateFunc } from '../../common/types';
import { getAddressLabelsByCountry, getEditAddressFormat } from '../../common/utils/KitAddress';
import { KitAddressField } from '../KitAddress/KitAddress';
import { KitFlexCol, KitFlexRow } from '../KitFlexbox';
import KitForm from '../KitFormComponents/KitForm';
import { spacing } from '../../common/theme/constants';

const KitFlexColSpaced = styled(KitFlexCol)`
  justify-content: space-between;
  flex: 1;
`;

const KitFlexRowSpaced = styled(KitFlexRow)`
  justify-content: space-between;
  gap: ${spacing.absolute.m}px;
`;

export const StyledDiv = styled.div`
  margin-bottom: ${spacing.absolute.m}px;
`;

export interface ContactInfo {
  address1: string;
  address2?: string;
  city: string;
  countryId?: number;
  stateId?: number;
  zipCode: string;
}

export interface KitEditAddressOptions {
  useDefaultAddressData?: boolean;
  isPlain?: boolean;
  hiddenFields?: string[];
  requiredFields?: string[];
}

interface State {
  id: number;
  name: string;
  code?: string;
}

interface Error {
  errorField: string;
  errorMessage: string;
}

interface SelectOption extends Option {
  code?: string;
}

export interface KitAddressProps {
  contactInfo: ContactInfo;
  countryFormats?: { [countryCode: string]: KitAddressField };
  countries: Country[];
  states: State[];
  options?: KitEditAddressOptions;
  onChange: (field, value) => void;
  validationErrors: Error[];
  requiredFields;
  t: TranslateFunc;
}

const KitEditAddress = ({
  contactInfo,
  t,
  countryFormats = {},
  options = {},
  countries,
  states,
  onChange,
  requiredFields,
  validationErrors = [],
}: KitAddressProps): JSX.Element => {
  const [updatedCountries, setUpdatedCountries] = useState<SelectOption[]>([]);
  const [updatedStates, setUpdatedStates] = useState<SelectOption[]>([]);
  const [selectedCountry, setSelectedCountry] = useState<SelectOption>(null);
  const [selectedState, setSelectedState] = useState<SelectOption>(null);
  const [errors, setErrors] = useState({});

  const extraOptions =
    countryFormats && !options.useDefaultAddressData
      ? { ...options, countryCode: selectedCountry?.code?.toLowerCase(), data: countryFormats }
      : { ...options, countryCode: selectedCountry?.code?.toLowerCase() };
  const { hiddenFields, isPlain } = extraOptions;

  const onChangeField = (field, value) => {
    onChange(field, value);
  };

  const getContactItem = (field) => {
    if (hiddenFields?.includes(field) || field === ',') {
      return null;
    }
    // field parameter is left intentionally
    const label = getAddressLabelsByCountry(field, extraOptions);
    const isSelectField =
      field?.toLowerCase().includes('country') || field?.toLowerCase().includes('state');

    let selectOptions = [];
    let selectField = null;

    if (isSelectField) {
      selectOptions = field?.toLowerCase().includes('country') ? updatedCountries : updatedStates;
      selectField = field?.toLowerCase().includes('country') ? selectedCountry : selectedState;
    }
    const hasError = Boolean(errors[field]);
    return isSelectField ? (
      <KitForm.Group key={`div${field}`}>
        <KitForm.Select
          key={`${field}Wrapper`}
          id={field}
          data-qa-id={field}
          options={selectOptions}
          placeholder={t(`kit:address.${label}`)}
          onChange={(e: { value: string; label: string }) => onChangeField(field, e.value)}
          value={selectField}
          isError={hasError}
          label={t(`kit:address.${label}`)}
          required={requiredFields?.includes(field)}
          infoMessage={hasError && errors[field].errorMessage}
        />
      </KitForm.Group>
    ) : (
      <KitForm.Group key={`div${field}`}>
        <KitForm.Input
          key={field}
          id={field}
          data-qa-id={field}
          onChange={(e) => onChangeField(field, e.target.value)}
          value={contactInfo[field]}
          isError={hasError}
          label={t(`kit:address.${label}`)}
          required={requiredFields?.includes(field)}
          infoMessage={hasError && errors[field].errorMessage}
        />
      </KitForm.Group>
    );
  };

  const formatter = getEditAddressFormat(extraOptions);

  useEffect(() => {
    if (countries?.length) {
      setUpdatedCountries(
        countries.map(({ id, name, code }) => ({ value: id, label: name, code }))
      );
    }
  }, [countries]);

  useEffect(() => {
    if (states?.length) {
      setUpdatedStates(states.map(({ id, name }) => ({ value: id, label: name })));
    }
  }, [states]);

  useEffect(() => {
    const viErrors = {};
    validationErrors.forEach((vError) => {
      viErrors[vError.errorField] = vError;
    });
    setErrors(viErrors);
  }, [validationErrors]);

  useEffect(() => {
    if (contactInfo?.countryId && updatedCountries?.length) {
      const country = updatedCountries.find(({ value }) => value === contactInfo?.countryId);
      if (country) {
        setSelectedCountry(country);
      }
    }
  }, [contactInfo?.countryId, updatedCountries]);

  useEffect(() => {
    if (contactInfo?.stateId && updatedStates?.length) {
      const state = updatedStates.find(({ value }) => value === contactInfo?.stateId);
      if (state) {
        setSelectedState(state);
      }
    } else {
      setSelectedState(null);
    }
  }, [contactInfo?.stateId, updatedStates, contactInfo?.countryId]);

  if (isPlain) {
    const flatFormatter = formatter.flat();
    return <>{flatFormatter.map((innerFormat) => getContactItem(innerFormat))}</>;
  }

  return (
    <>
      {formatter.map((format) => {
        if (typeof format === 'string') {
          return getContactItem(format);
        }
        return (
          <KitFlexRowSpaced key={`container_for_${format[0]}`}>
            {format.map((innerFormat) => (
              <KitFlexColSpaced key={`wrapper_${innerFormat}`} className='kit-flex-col'>
                {getContactItem(innerFormat)}
              </KitFlexColSpaced>
            ))}
          </KitFlexRowSpaced>
        );
      })}
    </>
  );
};

export default KitEditAddress;
